import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import api from '@/services/api'
import { toast } from 'react-hot-toast'

export interface ShiftState {
  isLoading: boolean
  error: string | null
  hasActiveShift: boolean
  currentShift: any | null
}

const initialState: ShiftState = {
  isLoading: false,
  error: null,
  hasActiveShift: false,
  currentShift: null,
}

const shiftSlice = createSlice({
  name: 'shift',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setHasActiveShift: (state, action: PayloadAction<boolean>) => {
      state.hasActiveShift = action.payload
    },
    setCurrentShift: (state, action: PayloadAction<any>) => {
      state.currentShift = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(openShift.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(openShift.fulfilled, (state, action) => {
        state.isLoading = false
        state.hasActiveShift = true
        state.currentShift = action.payload
        state.error = null
        localStorage.setItem('has_active_shift', 'true')
        localStorage.setItem('current_shift', JSON.stringify(action.payload))
      })
      .addCase(openShift.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        toast.error(action.payload as string)
      })
      .addCase(checkActiveShift.pending, (state) => {
        state.isLoading = true
      })
      .addCase(checkActiveShift.fulfilled, (state, action) => {
        state.isLoading = false
        state.hasActiveShift = action.payload.hasActiveShift
        state.currentShift = action.payload.shift
      })
      .addCase(checkActiveShift.rejected, (state, action) => {
        state.isLoading = false
        state.hasActiveShift = false
        state.currentShift = null
        state.error = action.payload as string
      })
      .addCase(closeShift.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(closeShift.fulfilled, (state) => {
        state.isLoading = false
        state.hasActiveShift = false
        state.currentShift = null
        state.error = null
        localStorage.removeItem('has_active_shift')
        localStorage.removeItem('current_shift')
      })
      .addCase(closeShift.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        toast.error(action.payload as string)
      })
  },
})

// Async Thunks
export const openShift = createAsyncThunk(
  'shift/openShift',
  async ({ openingBalance }: { openingBalance: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/cashier/shifts', {
        opening_balance: openingBalance,
      })

      // Handle different response formats
      if (response.data.success === true || response.data === 'success' || response.data.message === 'success') {
        // Return the data if available, or a success indicator
        return response.data.data || response.data || { success: true }
      } else if (response.status === 200 || response.status === 201) {
        // If status is success but no success flag, assume it worked
        return response.data.data || response.data || { success: true }
      } else {
        return rejectWithValue(response.data.message || 'Failed to open shift')
      }
    } catch (error: any) {
      // Check if error response contains success message
      if (error.response?.data?.message === 'success' || error.response?.data === 'success') {
        return { success: true }
      }
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to open shift')
    }
  }
)

export const checkActiveShift = createAsyncThunk(
  'shift/checkActiveShift',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/cashier/shifts/active')
      
      if (response.data.success) {
        return {
          hasActiveShift: response.data.data.has_active_shift,
          shift: response.data.data.shift,
        }
      } else {
        return rejectWithValue(response.data.message || 'Failed to check shift status')
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to check shift status')
    }
  }
)

export const closeShift = createAsyncThunk(
  'shift/closeShift',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/cashier/shifts/close')
      
      if (response.data.success) {
        return response.data.data
      } else {
        return rejectWithValue(response.data.message || 'Failed to close shift')
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to close shift')
    }
  }
)

export const { clearError, setLoading, setError, setHasActiveShift, setCurrentShift } = shiftSlice.actions
export default shiftSlice.reducer